import { useQuery } from "@tanstack/react-query";
import { useRealtimeConnected } from "@/app/providers/RealtimeProvider";
import { formatCfApiErrorMessage } from "@/shared/lib/cf-api-error-messages";
import { config } from "@/shared/lib/config";
import { handleError } from "@/shared/lib/fetcher";

export class WorkspaceRequestError extends Error {
  constructor(
    public code: string,
    public status: number,
    public details?: unknown,
  ) {
    super(code);
  }
}
export async function workspaceRequest<T>(
  path: string,
  body?: unknown,
  signal?: AbortSignal,
): Promise<T> {
  const response = await fetch(`${config.apiUrl}/v1/live-workspaces${path}`, {
    credentials: "include",
    signal,
    ...(body === undefined
      ? {}
      : {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(body),
        }),
  });
  if (response.status === 401 || response.status === 403)
    return handleError(response);
  const data = await response.json();
  if (!response.ok)
    throw new WorkspaceRequestError(
      data.code ?? "REQUEST_FAILED",
      response.status,
      data.details,
    );
  return data as T;
}
/** SSE ดูแลการเปลี่ยนค่า; ถ้าหลุดให้กลับไป polling เร็ว */
export function useWorkspacePollInterval() {
  return useRealtimeConnected() ? 15_000 : 1500;
}
export function useWorkspaceEnabled(enabled = true) {
  return useQuery({
    queryKey: ["live-workspace-capabilities"],
    enabled,
    queryFn: () => workspaceRequest<{ enabled: boolean }>("/capabilities"),
    staleTime: 60_000,
    retry: false,
  });
}
export function workspaceErrorMessage(error: unknown): string {
  const code = error instanceof Error ? error.message : "REQUEST_FAILED";
  const messages: Record<string, string> = {
    REVISION_CONFLICT: "ມີຄົນອື່ນແກ້ໄຂແລ້ວ. ໂຫຼດຂໍ້ມູນໃໝ່ ແລະກວດກາກ່ອນບັນທຶກອີກຄັ້ງ",
    PRODUCT_NOT_READY: "ສິນຄ້າຍັງບໍ່ພ້ອມຂາຍ: ກວດສະຕັອກ ແລະ preorder",
    PRODUCT_HAS_ORDERS: "ສິນຄ້ານີ້ມີອໍເດີ້ແລ້ວ ຈຶ່ງນຳອອກບໍ່ໄດ້; ສາມາດປິດຂາຍໄດ້",
    PRODUCTS_NOT_READY: "ມີສິນຄ້າຍັງບໍ່ພ້ອມ. ປິດຂາຍລາຍການນັ້ນກ່ອນເປີດຮັບ CF",
    OPTION_NOT_COMPATIBLE: "ບໍລິການເສີມນີ້ໃຊ້ກັບສິນຄ້າບໍ່ໄດ້",
    PRICE_NOT_REPRESENTABLE: "ສ່ວນຫຼຸດຕ້ອງເປັນຫຼັກພັນ. ກວດລາຄາທີ່ລະບົບເຮັດໄດ້ກ່ອນບັນທຶກ",
    LIVE_IMPORT_CONFLICT: "ລາຍການໃນ live ມີລະຫັດ ຫຼືລາຄາຂັດກັນ",
    LIVE_OPTIONS_CONFLICT: "ບໍລິການເສີມໃນ live ຂັດກັບງານທີ່ກຽມໄວ້",
    LIVE_PROMO_OR_WAREHOUSE_CONFLICT: "ໂປຣໂມຊັນ ຫຼືສາງໃນ live ບໍ່ກົງກັນ",
    DISPLAY_PROFILE_CONFLICT: "live ນີ້ມີຈໍ OBS ຕັ້ງໄວ້ແລ້ວ. ກວດລາຍການທີ່ຂັດກັນກ່ອນ",
    CLOSE_ORDERING_BEFORE_IMPORT: "ປິດຮັບ CF ຂອງ live ປາຍທາງກ່ອນນຳເຂົ້າ",
    CLOSE_ORDERING_BEFORE_WAREHOUSE_CHANGE: "ປິດຮັບ CF ກ່ອນປ່ຽນສາງ",
    CONNECT_LIVE_FIRST: "ຜູກ Facebook Live ກ່ອນເປີດຮັບ CF",
    CF_CODE_CONFLICT: "ລະຫັດ CF ຊ້ຳກັບລາຍການທີ່ມີແລ້ວ",
    INVALID_OR_DUPLICATE_CF_CODE: "ລະຫັດ CF ບໍ່ຖືກຕ້ອງ ຫຼືຊ້ຳ",
    FEATURE_DISABLED: "ສ່ວນຄວບຄຸມນີ້ຍັງບໍ່ເປີດໃຊ້",
    WEBHOOK_SUBSCRIBE_FAILED:
      "ເປີດຮັບຄອມເມັນຈາກ Facebook ບໍ່ສຳເລັດ. CF ຍັງປິດຢູ່ ກະລຸນາລອງໃໝ່",
    PAGE_DISCONNECTED: "ກະລຸນາເຊື່ອມ Facebook Page ໃໝ່ກ່ອນເປີດຮັບ CF",
  };
  return (
    messages[code] ??
    (/^[A-Z_]+$/.test(code) ? formatCfApiErrorMessage(code) : null) ??
    "ບັນທຶກບໍ່ສຳເລັດ. ຂໍ້ມູນທີ່ກຳລັງແກ້ຍັງຢູ່ ກະລຸນາລອງອີກຄັ້ງ"
  );
}
